import { ActivatedChallengeApprovalDetails, GetActivatedChallengeResponse } from '../types';
import { LitElement, css, html } from 'lit';
import { approve_activity_checkin, approve_location_checkin, reject_activity_checkin, reject_location_checkin } from '../helpers/api/challenge-api';
import { customElement, property, query, state } from 'lit/decorators';

import { getMainPage } from '../helpers/page-name';

@customElement('challenge-approvals-section')
export class ChallengeApprovalsSection extends LitElement {

    @query('#search')
    searchInput!: HTMLInputElement;

    @property({ type: Array })
    data: ActivatedChallengeApprovalDetails[] = [];

    @property({ type: Array })
    approvals: ActivatedChallengeApprovalDetails[] = [];

    @state()
    sortBy: string = '';

    @state()
    sortOrder: 'asc' | 'desc' = 'asc';

    async updated(changedProperties: any) {
        changedProperties.forEach((oldValue: any, propName: any) => {
            if(propName === 'data') {
                this.approvals = this.data ?? [];
            }
        });
    }

    private async approveLocationCheckin(activated_challenge_id: string, location_id: string) {
        let updatedChallenge: GetActivatedChallengeResponse | undefined;
        try {
            updatedChallenge = await approve_location_checkin(activated_challenge_id, location_id);
        } catch(e) {
            getMainPage().errorToastElement.showToastAndThrow('Error approving location checkin. Reminder: You cannot approve your own check-ins.');
        }

        if(updatedChallenge) {
            this.dispatchEvent(new CustomEvent('updated-approvals'));
        }
    }

    private async rejectLocationCheckin(activated_challenge_id: string, location_id: string) {
        try {
            await reject_location_checkin(activated_challenge_id, location_id);
            this.dispatchEvent(new CustomEvent('updated-approvals'));
        } catch(e) {
            getMainPage().errorToastElement.showToastAndThrow('Error rejecting location checkin. Reminder: You cannot reject your own check-ins.');
        }
    }

    private async rejectActivityCheckin(activated_challenge_id: string, activity_id: string) {
        try {
            await reject_activity_checkin(activated_challenge_id, activity_id);
            this.dispatchEvent(new CustomEvent('updated-approvals'));
        } catch(e) {
            getMainPage().errorToastElement.showToastAndThrow('Error rejecting activity checkin. Reminder: You cannot reject your own check-ins.');
        }
    }

    private async approveActivityCheckin(activated_challenge_id: string, activity_id: string) {
        let updatedChallenge: GetActivatedChallengeResponse | undefined;
        try {
            updatedChallenge = await approve_activity_checkin(activated_challenge_id, activity_id);
        } catch(e) {
            getMainPage().errorToastElement.showToastAndThrow('Error approving activity checkin. Reminder: You cannot approve your own check-ins.');
        }

        if(updatedChallenge) {
            this.dispatchEvent(new CustomEvent('updated-approvals'));
        }
    }

    async clickApprove(type:'location'|'activity', activated_challenge_id: string, id: string) {
        if(type === 'location') {
            await this.approveLocationCheckin(activated_challenge_id, id);
        } else {
            await this.approveActivityCheckin(activated_challenge_id, id);
        }
    }

    async clickReject(type:'location'|'activity', activated_challenge_id: string, id: string) {
        if(type === 'location') {
            await this.rejectLocationCheckin(activated_challenge_id, id);
        } else {
            await this.rejectActivityCheckin(activated_challenge_id, id);
        }
    }

    async approveAll() {
        if (confirm('Are you sure you want to approve all?')) {
            for (const approval of this.approvals) {
                if (approval.type === 'location') {
                    await this.approveLocationCheckin(approval.activated_challenge_id, approval.location_activity_id);
                } else {
                    await this.approveActivityCheckin(approval.activated_challenge_id, approval.location_activity_id);
                }
            }
        }
    }

    render() {
        return html`
            <div class="modalContent">
                <div class="modal__header">
                        <b>Points Awaiting Approval</b>
                        <form-button
                            size="xs"
                            variant="outline"
                            @click="${this.approveAll}"
                        >
                            Approve All
                        </form-button>
                </div>
                <br />
                <div class="challenges-wrapper">
                    ${this.approvals.map((approval) => html`
                        <div class="challenge-row">
                            <div class="email data-cell text primary">${approval.email ?? approval.phone_number}( ${approval.username} )</div>
                            ${approval.activity_submission_type === 'text' ?
                                html`<div class="checkin data-cell text ">${approval.location_activity_name}<br>${approval.submission[1].join(', ')}</div>` :
                                approval.activity_submission_type === 'photos' ?
                                html`<div class="checkin data-cell text">${approval.location_activity_name}<br>${approval.submission[1].map((s) => html`<a href="${s}" target="_blank">View</a>`)}</div>` :
                                approval.activity_submission_type === 'receipt' ?
                                html`<div class="checkin data-cell text">${approval.location_activity_name}<br>Total Spend: ${approval.submission[0]}<br><a href="${approval.submission[1][0]}" target="_blank">View Receipt</a></div>` :
                                approval.activity_submission_type === 'url' ?
                                html`<div class="checkin data-cell text">${approval.location_activity_name}<br>${approval.submission[1].map((s) => html`<a href="${s}" target="_blank">${s}</a>`)}</div>` :
                            ''}
                            <div class="checkin data-cell text">
                                <form-button size="xs" variant="outline" @click="${async () => await this.clickApprove(approval.type === 'location' ? 'location' : 'activity', approval.activated_challenge_id, approval.location_activity_id)}">Approve</form-button> <br>
                                <form-button size="xs" variant="outlinewarning" @click="${async () => await this.clickReject(approval.type === 'location' ? 'location' : 'activity', approval.activated_challenge_id, approval.location_activity_id)}">Reject</form-button>
                            </div>
                        </div>
                    `)}
                </div>
            </div>

        `;
    }


    static get styles() {
        return css`
            :host {
                --modal-width: 800px;
            }

            .modalContent {
                position: relative;
                width: 100%;
                max-height: calc(80vh - 180px);
                height: auto;
                overflow-y: auto;
                box-sizing: border-box;
            }

            .modal__header {
                width: 100%;
                display: flex;
                align-items: center;
                flex-direction: row;
                justify-content: space-between;
            }

            .modal__content {
                padding: 20px 0 5px;
                width: 100%;
                box-sizing: border-box;
            }

            .modal__footer {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 20px 0;
                box-sizing: border-box;
            }

            .header-cell,
            .data-cell {
                align-items: center;
            }

            .data-cell {
                display: flex;
                align-items: flex-start;
                padding: 0 10px;
                flex-direction: column;
                flex-wrap: wrap;
            }

            .data-cell.text {
                text-align: left;
                width: 20%;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .data-cell.number {
                text-align: center;
                width: 10%;
                justify-content: center;
            }

            .data-cell.primary {
                width: 40%;
            }

            .action-button {
                font-size: 16px;
                border-radius: 8px;
                margin: 10px 0;
                --mdc-typography-button-text-transform: capitalize;
            }

            .aqua-blue {
                --mdc-theme-primary: var(--main-secondary-color);
            }

            .challenges-wrapper {
                width: 100%;
                max-height: 330px;
                overflow-y: auto;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: flex-start;
                box-sizing: border-box;
            }

            .challenge-row {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 8px 5px;
                box-sizing: border-box;
                border-bottom: 1px solid #DADADA;
                gap: 10px;
            }

            .challenge-row:first-child {
                border-top: 1px solid #DADADA;
            }

            .user {
                color: #102C3D;
                text-align: left;
                line-height: 125%;
            }

            .stats {
                box-sizing: border-box;
                color: #102C3D;
                text-align: center;
                line-height: 125%;
            }

            @media only screen and (max-width: 550px) {
                .modalContent {
                    width: 100%;
                    min-height: 100%;
                    max-height: 100%;
                }

                .terms__desc-wrapper {
                    max-height: 200px;
                }
            }
        `;
    }
}